import { Select } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import Spinner from '@/components/Common/Spinner';
import SearchInput from '@/components/Inputs/SearchInput';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { modalStyle } from '@/styles/style';
import { lowerCaseMatch } from '@/utils/util';

import type { DiscordChannelT } from '../types/types';
import DiscordWorkspaceChannelItem from './DiscordWorkspaceChannelItem';
import { useDiscord, useDiscordAction, useDiscordChannels } from './useDiscord';

type Props = {
  discordWorkspaceId: string;
};

const PreiodOptions: BaseOptionType[] = [
  { value: 12, label: '12 hour' },
  { value: 24, label: '24 hour' },
];

const DiscordWorkspaceChannelModal = (props: Props) => {
  const color = useColor();
  const { discordWorkspaces } = useDiscord(true);
  const discordWorkspace = discordWorkspaces.find(
    (w) => w.alias === props.discordWorkspaceId,
  );
  const { closeModal } = useModalContext();
  const [search, setSearch] = useState('');

  const { channels } = useDiscordChannels(props.discordWorkspaceId);

  const { pushChannelMutation, pullChannelMutation, updatePeriodMutation } =
    useDiscordAction();

  const shownChannels = useMemo(() => {
    const newChannels = { ...channels };

    // key 순서: "No Category"를 항상 맨 앞으로
    const keys = Object.keys(newChannels);
    const sortedKeys = [
      ...keys.filter((k) => k === 'No Category'),
      ...keys.filter((k) => k !== 'No Category'),
    ];

    const sortedChannels: Record<string, DiscordChannelT[]> = {};

    for (const key of sortedKeys) {
      const list = newChannels[key];
      const filtered = search
        ? list.filter((d) => lowerCaseMatch(d.name, search))
        : list; // 검색어 없으면 전체 반환

      sortedChannels[key] = filtered;
    }

    return sortedChannels;
  }, [channels, search]);

  const currentChannels = useMemo(() => {
    return discordWorkspace?.channels || [];
  }, [discordWorkspace]);

  const handleOnChangeActive = (channel: DiscordChannelT) => {
    if (!discordWorkspace) return;
    if (currentChannels.some((c) => c.id === channel.id)) {
      pullChannelMutation({
        alias: discordWorkspace.alias,
        channelIds: [channel.id],
      });
    } else {
      pushChannelMutation({
        alias: discordWorkspace.alias,
        channels: currentChannels
          .filter((c) => c.id !== channel.id)
          .concat([channel]),
      });
    }
  };

  const selectAllCategoryChannelsHandler = (categoryName: string) => {
    if (!discordWorkspace) return;
    const targets = channels?.[categoryName] || [];
    pushChannelMutation({
      alias: discordWorkspace.alias,
      channels: currentChannels.concat(targets).reduce((acc, value) => {
        if (acc.some((c) => c.id === value.id)) return acc;
        acc.push(value);
        return acc;
      }, [] as DiscordChannelT[]),
    });
  };

  const deselectAllCategoryChannelHandler = (categoryName: string) => {
    if (!discordWorkspace) return;
    const targets = channels?.[categoryName] || [];
    pullChannelMutation({
      alias: discordWorkspace.alias,
      channelIds: targets.map((c) => c.id),
    });
  };

  const handlePeriodChangeHandler = (value: number) => {
    if (!discordWorkspace) return;
    updatePeriodMutation({
      alias: discordWorkspace.alias,
      period: Number(value),
    });
  };

  return (
    <ScrollView style={{ maxHeight: 'calc(100vh - 80px' }}>
      <Style d="column">
        <Heading type="h2" color={color.gray800}>
          {discordWorkspace?.name || 'Discord'}
        </Heading>
        <Space h="20px" />
        <Flex j="space-between" a="center">
          <Typo type="label1" color={color.gray800}>
            Select channels to sync
          </Typo>
          <SearchInput
            placeholder="Search channel"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </Flex>
        <Space h="12px" />
        <Flex className="channel-list-wrapper">
          {shownChannels ? (
            <VFlex style={{ width: '100%' }}>
              <ScrollView style={{ width: '100%' }}>
                {Object.keys(shownChannels).map((categoryName, idx) => {
                  const newChannels = shownChannels[categoryName];
                  return (
                    <VFlex
                      key={idx}
                      a="flex-start"
                      g="8px"
                      style={{ width: '100%' }}
                    >
                      <CategoryWrapper j="space-between">
                        <Typo type="label2" weight="sb" color={color.gray800}>
                          {categoryName}
                        </Typo>
                        <Flex g="24px">
                          <Typo
                            type="label3"
                            color={
                              search.length > 0
                                ? color.gray500
                                : color.primary100
                            }
                            style={{
                              cursor:
                                search.length > 0 ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() =>
                              search === '' &&
                              selectAllCategoryChannelsHandler(categoryName)
                            }
                          >
                            Select all
                          </Typo>
                          <Divider
                            direction="v"
                            length={14}
                            backgroundColor={color.gray400}
                          />
                          <Typo
                            type="label3"
                            color={
                              search.length > 0
                                ? color.gray500
                                : color.primary100
                            }
                            style={{
                              cursor:
                                search.length > 0 ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() =>
                              search === '' &&
                              deselectAllCategoryChannelHandler(categoryName)
                            }
                          >
                            Deselect all
                          </Typo>
                        </Flex>
                      </CategoryWrapper>
                      <Flex
                        g="8px"
                        j="space-between"
                        style={{
                          flexWrap: 'wrap',
                          width: '100%',
                          padding: '0px 24px 24px 24px',
                        }}
                      >
                        {newChannels.map((data) => {
                          return (
                            <DiscordWorkspaceChannelItem
                              channel={data}
                              isActive={currentChannels.some(
                                (c) => c.id === data.id,
                              )}
                              onChangeActive={handleOnChangeActive}
                              key={data.name}
                            />
                          );
                        })}
                      </Flex>
                    </VFlex>
                  );
                })}
              </ScrollView>
            </VFlex>
          ) : (
            <Flex
              a="center"
              j="center"
              style={{ width: '100%', height: '100%' }}
              d="column"
              g="16px"
            >
              <Spinner />
              <Typo type="label2" color={color.gray500}>
                Loading Channel list...
              </Typo>
            </Flex>
          )}
        </Flex>
        <Space h="24px" />
        <Flex d="column" g="12px">
          <Typo type="label1" color={color.gray800} weight="m">
            Schedule periodic sync
          </Typo>
          <Flex className="periodic-wrapper">
            <Flex d="column" g="16px">
              <Typo type="label2" weight="m" color={color.gray700}>
                Select when data will be pushed to Syncly
              </Typo>
              <Flex d="row" g="16px" a="center" style={{ height: 32 }}>
                <Typo type="label2" color={color.gray700}>
                  Repeat every
                </Typo>
                <Select
                  getPopupContainer={(triggerNode) => {
                    return triggerNode.parentNode as HTMLDivElement;
                  }}
                  style={{ width: 220 }}
                  size="large"
                  options={PreiodOptions}
                  onChange={(v) => handlePeriodChangeHandler(v)}
                  defaultValue={discordWorkspace?.period}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Space h="32px" />
        <Flex j="flex-end">
          <Button sizeType="m" onClick={closeModal} style={{ width: 120 }}>
            Done
          </Button>
        </Flex>
      </Style>
    </ScrollView>
  );
};

export default DiscordWorkspaceChannelModal;

const Style = styled(Flex)`
  padding: 24px;
  width: 800px;
  ${modalStyle};
  .channel-list-wrapper {
    min-width: 100%;
    height: 400px;
    border: 1px solid ${(props) => props.theme.colors.gray300};
    border-radius: 8px;
  }
  .periodic-wrapper {
    height: 110px;
    border: 1px solid ${(props) => props.theme.colors.gray300};
    border-radius: 8px;
    padding: 24px;
  }
`;

const CategoryWrapper = styled(Flex)`
  padding: 24px 24px 0px 24px;
  width: 100%;
`;
